import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { ConsentTextVersionRow } from '@/hooks/useConsentRecords';
import {
  useCreateConsentRequest,
  type ConsentRequestWithItems,
  type SendConsentRequestOutput,
} from '@/hooks/useConsentRequests';
import {
  PORTAL_RECORDABLE_CONSENT_TYPES,
  effectiveConsentTextVersion,
  isConsentRequestOpen,
  type ConsentTypeKey,
} from '@/lib/consent/consentTypes';

// The database caps the deadline at 90 days; 60 leaves room for client clock skew.
const DEADLINE_DAYS = [7, 14, 30, 60] as const;

export function notificationToastKey(notification: SendConsentRequestOutput | null): {
  key: string;
  tone: 'success' | 'warning';
} {
  if (!notification) return { key: 'consent.request.created_notify_unknown', tone: 'warning' };
  if (notification.status === 'sent') {
    return {
      key: notification.channel === 'sms' ? 'consent.request.sent_sms' : 'consent.request.sent_email',
      tone: 'success',
    };
  }
  return { key: 'consent.request.created_not_notified', tone: 'warning' };
}

export function RequestConsentSlideOver({
  open,
  onClose,
  clinicId,
  patientId,
  hasPortalAccess,
  textVersions,
  requests,
}: {
  open: boolean;
  onClose: () => void;
  clinicId: string;
  patientId: string;
  hasPortalAccess: boolean;
  textVersions: ConsentTextVersionRow[];
  requests: ConsentRequestWithItems[];
}) {
  const { t } = useTranslation();
  const create = useCreateConsentRequest();
  const [selected, setSelected] = useState<ConsentTypeKey[]>([]);
  const [days, setDays] = useState<number>(30);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setDays(30);
  }, [open]);

  const pendingTypes = useMemo(() => {
    const set = new Set<ConsentTypeKey>();
    for (const request of requests) {
      if (!isConsentRequestOpen(request)) continue;
      for (const item of request.consent_request_items) {
        if (!item.response_record_id) set.add(item.consent_type);
      }
    }
    return set;
  }, [requests]);

  const rows = PORTAL_RECORDABLE_CONSENT_TYPES.map((type) => ({
    type,
    version: effectiveConsentTextVersion(textVersions, clinicId, type),
    pending: pendingTypes.has(type),
  }));

  const toggle = (type: ConsentTypeKey, checked: boolean) =>
    setSelected((prev) => (checked ? [...prev, type] : prev.filter((x) => x !== type)));

  const handleSend = async () => {
    const ids = rows
      .filter((row) => selected.includes(row.type) && row.version && !row.pending)
      .map((row) => row.version!.id);
    if (ids.length === 0) return;
    try {
      const result = await create.mutateAsync({
        patientId,
        textVersionIds: ids,
        expiresAt: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
      });
      const { key, tone } = notificationToastKey(result.notification);
      if (tone === 'success') toast.success(t(key));
      else toast(t(key));
      onClose();
    } catch (err) {
      console.error('create_consent_request failed', err);
      toast.error(t('consent.request.save_error'));
    }
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t('consent.request.title')}
      description={t('consent.request.description')}
      dirty={selected.length > 0}
      footer={
        <SlideOverFooter
          onCancel={onClose}
          primary={
            <Button onClick={() => void handleSend()} loading={create.isPending} disabled={selected.length === 0}>
              {t('consent.request.send')}
            </Button>
          }
        />
      }
    >
      <div className="space-y-s4">
        {!hasPortalAccess ? <Banner tone="warning">{t('consent.request.no_portal')}</Banner> : null}
        <fieldset className="space-y-s2">
          <legend className={cn(TYPE.small, 'mb-s2 font-medium text-ds-text')}>{t('consent.request.choose')}</legend>
          {rows.map((row) => (
            <Checkbox
              key={row.type}
              id={`consent-request-${row.type}`}
              checked={selected.includes(row.type)}
              disabled={!row.version || row.pending}
              onChange={(e) => toggle(row.type, e.target.checked)}
              label={t(`consent.types.${row.type}`)}
              hint={
                row.pending
                  ? t('consent.request.already_pending')
                  : row.version
                    ? `${row.version.version_label} · ${
                        row.version.clinic_id ? t('consent.clinic_text') : t('consent.clinio_default')
                      }`
                    : t('consent.request.no_text')
              }
            />
          ))}
        </fieldset>
        <Select
          id="consent-request-deadline"
          label={t('consent.request.deadline')}
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {DEADLINE_DAYS.map((d) => (
            <option key={d} value={d}>
              {t('consent.request.deadline_days', { count: d })}
            </option>
          ))}
        </Select>
        <p className={cn(TYPE.small, 'text-ds-muted')}>{t('consent.request.privacy_hint')}</p>
      </div>
    </SlideOver>
  );
}
